import React, { useMemo, useState } from "react";
import { Alert, Button, Card, Form, Input, Space, Typography, message } from "antd";
import {
    InfoCircleOutlined,
    ArrowLeftOutlined,
    LinkOutlined,
    PlayCircleOutlined,
    CheckCircleOutlined
} from "@ant-design/icons";
import { Duration } from "luxon";
import { connect } from "react-redux";
import { API_URL } from "../../const";
import { createNewVideo } from "../../actions/contentCreation";
import SmartSlidesLogo from "./logo/SmartSlides.png";
import "./NewVideo.less";

const { Title, Text } = Typography;

const YouTubeIcon = () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
            d="M21.58 7.2a2.98 2.98 0 0 0-2.1-2.1C17.62 4.5 12 4.5 12 4.5s-5.62 0-7.48.6a2.98 2.98 0 0 0-2.1 2.1c-.6 1.86-.6 4.8-.6 4.8s0 2.94.6 4.8a2.98 2.98 0 0 0 2.1 2.1c1.86.6 7.48.6 7.48.6s5.62 0 7.48-.6a2.98 2.98 0 0 0 2.1-2.1c.6-1.86.6-4.8.6-4.8s0-2.94-.6-4.8Z"
            fill="#FF0000"
        />
        <path d="M10 15.5v-7l6 3.5-6 3.5Z" fill="#FFFFFF" />
    </svg>
);

const VimeoIcon = () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="2" y="2" width="20" height="20" rx="10" fill="#1AB7EA" />
        <path
            d="M17.25 8.24c-.07 1.62-1.2 3.83-3.4 6.62-2.28 2.92-4.2 4.38-5.77 4.38-.97 0-1.79-.9-2.46-2.68L4.3 11.7c-.5-1.78-1.03-2.67-1.6-2.67-.12 0-.55.26-1.28.78l-.77-.99c.8-.7 1.6-1.4 2.38-2.1 1.07-.92 1.87-1.4 2.4-1.44 1.26-.12 2.03.75 2.32 2.62.3 2.03.5 3.3.58 3.8.32 1.68.67 2.52 1.06 2.52.3 0 .74-.48 1.34-1.44.6-.96.92-1.7.98-2.2.08-.84-.24-1.26-.98-1.26-.35 0-.71.08-1.08.24.72-2.35 2.1-3.5 4.13-3.46 1.5.04 2.2 1.02 2.13 2.96Z"
            fill="#FFFFFF"
        />
    </svg>
);

const SmartSlideIcon = () => (
    <img src={SmartSlidesLogo} alt="SmartSlides" />
);

const SOURCE_OPTIONS = [
    {
        key: "youtube",
        label: "Import from YouTube",
        description: "Paste a YouTube video URL to import into your library.",
        icon: <YouTubeIcon />
    },
    {
        key: "vimeo",
        label: "Import from Vimeo",
        description: "Paste a Vimeo video URL to import into your library.",
        icon: <VimeoIcon />
    },
    {
        key: "smartslide",
        label: "Import from SmartSlide",
        description: "Upload your slides with SmartSlide to create a video, then import it into CUbits.",
        icon: <SmartSlideIcon />,
        disabled: false
    }
];

const UPLOAD_TYPE_MAP = {
    youtube: 2,
    vimeo: 3,
    smartslide: 4
};

const NewVideo = ({ onCancel, showCloseButton = false, createNewVideo }) => {
    const [step, setStep] = useState("select");
    const [source, setSource] = useState(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [importing, setImporting] = useState(false);
    const [previewVideo, setPreviewVideo] = useState(null);
    const [playlistNotice, setPlaylistNotice] = useState("");
    const [authenticating, setAuthenticating] = useState(false);
    const [smartSlideConnected, setSmartSlideConnected] = useState(false);
    const [smartSlideUrl, setSmartSlideUrl] = useState("");
    const [form] = Form.useForm();

    const sourceMeta = useMemo(
        () => SOURCE_OPTIONS.find((opt) => opt.key === source),
        [source]
    );

    const getAuthHeaders = () => {
        const headers = { "Content-Type": "application/json" };
        const token = localStorage.getItem("access");
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }
        return headers;
    };

    const normalizeFirstVideo = (payload) => {
        if (payload?.videoType === "youtube") {
            const first = payload?.data?.items?.[0];
            if (!first) {
                return null;
            }
            return {
                title: first?.snippet?.title || "Untitled video",
                videoType: "youtube",
                embedId: first?.id,
                duration: Duration.fromISO(first?.contentDetails?.duration || "PT0S").shiftTo("seconds").seconds || 0
            };
        }

        if (payload?.videoType === "vimeo") {
            if (payload?.contentType === "playlist") {
                const first = payload?.data?.data?.[0];
                if (!first) {
                    return null;
                }
                return {
                    title: first?.name || "Untitled video",
                    videoType: "vimeo",
                    embedId: (first?.uri || "").replace("/videos/", ""),
                    duration: first?.duration || 0
                };
            }

            const first = payload?.data?.[0];
            if (!first) {
                return null;
            }
            return {
                title: first?.name || "Untitled video",
                videoType: "vimeo",
                embedId: (first?.uri || "").replace("/videos/", ""),
                duration: first?.duration || 0
            };
        }

        return null;
    };

    const requestPreview = async ({ url }) => {
        setLoadingPreview(true);
        setPreviewVideo(null);
        setPlaylistNotice("");

        try {
            const response = await fetch(`${API_URL}/api/v1/retrieve-video-playlist-data/`, {
                method: "POST",
                headers: getAuthHeaders(),
                body: JSON.stringify({ url })
            });

            if (!response.ok) {
                throw new Error("Could not fetch video details.");
            }

            const data = await response.json();
            if (Array.isArray(data?.licensableContent) && data.licensableContent.length > 0) {
                throw new Error("This video is already imported by you or another user, so it cannot be imported again.");
            }

            if (data?.videoType !== source) {
                throw new Error(`Provided URL is not a ${sourceMeta?.label?.replace("Import from ", "") || "valid"} link.`);
            }

            const normalized = normalizeFirstVideo(data);
            if (!normalized || !normalized.embedId) {
                throw new Error("No valid video found in this URL.");
            }

            if (data?.contentType === "playlist" || (source === "youtube" && (data?.data?.items?.length || 0) > 1)) {
                setPlaylistNotice("Playlist/showcase detected. The first video will be imported.");
            }

            setPreviewVideo(normalized);
        } catch (error) {
            message.error(error?.message || "Failed to fetch video info.");
        } finally {
            setLoadingPreview(false);
        }
    };

    const authenticateSmartSlide = async () => {
        setAuthenticating(true);
        try {
            const response = await fetch(`${API_URL}/api/v1/smartslides/authenticate`, {
                method: "POST",
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                throw new Error("SmartSlide authentication failed.");
            }

            const data = await response.json().catch(() => ({}));
            if (data?.url) {
                setSmartSlideUrl(data.url);
                window.open(data.url, "_blank", "noopener,noreferrer");
            }

            setSmartSlideConnected(true);
            message.success("SmartSlide authenticated successfully.");
        } catch (error) {
            message.error(error?.message || "SmartSlide authentication failed.");
        } finally {
            setAuthenticating(false);
        }
    };

    const submitImport = async () => {
        if (!previewVideo) {
            return;
        }

        setImporting(true);
        try {
            const uploadType = UPLOAD_TYPE_MAP[previewVideo.videoType];
            const requestBody = {
                embed_id: previewVideo.embedId,
                video_type: previewVideo.videoType,
                title: previewVideo.title,
                duration: Math.round(previewVideo.duration || 0),
                upload_type: uploadType
            };
            await new Promise((resolve, reject) => {
                createNewVideo(
                    requestBody,
                    () => resolve(),
                    (error) => reject(error)
                );
            });
            message.success("Video imported successfully.");
            onCancel?.();
        } catch (error) {
            message.error(error?.message || "Video import failed.");
        } finally {
            setImporting(false);
        }
    };

    const handleChooseSource = (nextSource) => {
        const selectedSource = SOURCE_OPTIONS.find((item) => item.key === nextSource);
        if (selectedSource?.disabled) {
            message.info("SmartSlide import is coming soon.");
            return;
        }

        setSource(nextSource);
        setStep("url");
        setPreviewVideo(null);
        setPlaylistNotice("");
        form.resetFields();
    };

    const validateVideoUrl = (_, value) => {
        if (!value) {
            return Promise.reject(new Error("Please enter a URL"));
        }

        try {
            new URL(value);
        } catch {
            return Promise.reject(new Error("Please enter a valid URL"));
        }

        const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube|youtu|youtube-nocookie)\.(com|be)\//;
        const vimeoRegex = /^(https?:\/\/)?(www\.)?vimeo\.com\//;

        if (source === "youtube" && !youtubeRegex.test(value)) {
            return Promise.reject(new Error("Please enter a valid YouTube URL"));
        }

        if (source === "vimeo" && !vimeoRegex.test(value)) {
            return Promise.reject(new Error("Please enter a valid Vimeo URL"));
        }

        return Promise.resolve();
    };

    const handleBack = () => {
        setStep("select");
        setSource(null);
        setPreviewVideo(null);
        setPlaylistNotice("");
        form.resetFields();
    };

    return (
        <div className="new-video">
            {step === "select" && (
                <div className="new-video__select-view">
                    <div className="new-video__source-grid">
                        {SOURCE_OPTIONS.map((option) => (
                            <Card
                                key={option.key}
                                hoverable={!option.disabled}
                                className={`new-video__source-card ${option.disabled ? "new-video__source-card--disabled" : ""}`}
                                onClick={() => !option.disabled && handleChooseSource(option.key)}>
                                <div className="new-video__source-content">
                                    <div className="new-video__source-media">
                                        <span className="new-video__source-icon">{option.icon}</span>
                                    </div>
                                    <div className="new-video__source-details">
                                        <Text strong>{option.label}</Text>
                                        <Text type="secondary">{option.description}</Text>
                                        {option.disabled && <Text className="new-video__upcoming-text">Upcoming</Text>}
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                </div>
            )}

            {step === "url" && (
                <div className="new-video__url-view">
                    <Space className="new-video__header" align="center">
                        <Button type="text" icon={<ArrowLeftOutlined />} onClick={handleBack} />
                        <Title level={5} style={{ margin: 0 }}>
                            {sourceMeta?.label}
                        </Title>
                        <span className="new-video__header-source-icon">{sourceMeta?.icon}</span>
                    </Space>

                    {source === "smartslide" ? (
                        <div className="new-video__smartslide-auth">
                            <Card className="new-video__smartslide-card" title="SmartSlide" bordered={false}>
                                <Space direction="vertical" size={12} className="new-video__smartslide-content">
                                    <Text>Upload your slides with SmartSlide to create a video, then import it into CUbits.</Text>
                                    {smartSlideConnected && (
                                        <Text className="new-video__smartslide-connected">
                                            <CheckCircleOutlined /> Connected with SmartSlide
                                        </Text>
                                    )}
                                    <Space direction="vertical" size={8} className="new-video__smartslide-actions">
                                        {!smartSlideConnected && (
                                            <Button type="primary" loading={authenticating} onClick={authenticateSmartSlide}>
                                                Authenticate with SmartSlide
                                            </Button>
                                        )}
                                        {smartSlideConnected && smartSlideUrl && (
                                            <Button type="link" icon={<LinkOutlined />} onClick={() => window.open(smartSlideUrl, "_blank", "noopener,noreferrer")}>
                                                Open SmartSlide again
                                            </Button>
                                        )}
                                    </Space>
                                </Space>
                            </Card>
                        </div>
                    ) : (
                        <Form className="new-video__inline-form" layout="inline" form={form} onFinish={requestPreview}>
                            <Input.Group compact className="new-video__input-group">
                                <Form.Item
                                    className="new-video__inline-form-group-item"
                                    name="url"
                                    rules={[
                                        { validator: validateVideoUrl }
                                    ]}
                                    noStyle>
                                    <Input
                                        size="large"
                                        prefix={<LinkOutlined />}
                                        className="new-video__group-input"
                                        placeholder={source === "youtube" ? "YouTube URL: https://www.youtube.com/watch?v=..." : "Vimeo URL: https://vimeo.com/..."}
                                    />
                                </Form.Item>
                                <Button size="large" type="default" loading={loadingPreview} htmlType="submit">
                                    Load Summary
                                </Button>
                            </Input.Group>
                        </Form>
                    )}
                    {!previewVideo && source !== "smartslide" && (
                        <div className="new-video__url-help">
                            <Space size={8} align="start">
                                <LinkOutlined className="new-video__help-icon" />
                                <Text type="secondary">
                                    Supported links: {source === "youtube" ? "YouTube video or playlist URLs" : "Vimeo video or showcase URLs"}.
                                </Text>
                            </Space>
                            <Space size={8} align="start">
                                <InfoCircleOutlined className="new-video__help-icon" />
                                <Text type="secondary">
                                    We will fetch metadata first so you can review title, source, and duration before importing.
                                </Text>
                            </Space>
                            <Space size={8} align="start">
                                <CheckCircleOutlined className="new-video__help-icon" />
                                <Text type="secondary">
                                    After import, the new video will be added to your list automatically.
                                </Text>
                            </Space>
                        </div>
                    )}

                    {playlistNotice ? (
                        <Alert className="new-video__notice" type="info" message={playlistNotice} showIcon />
                    ) : null}

                    {previewVideo ? (
                        <Card className="new-video__preview-card" title="Video Summary">
                            <Space direction="vertical" size={6}>
                                <Text strong>{previewVideo.title}</Text>
                                <Text type="secondary">Source: {previewVideo.videoType.toUpperCase()}</Text>
                                <Text type="secondary">Embed ID: {previewVideo.embedId}</Text>
                                <Text type="secondary">Duration: {Math.round(previewVideo.duration || 0)}s</Text>
                            </Space>

                            <div className="new-video__preview-actions">
                                <Button icon={<PlayCircleOutlined />} onClick={() => window.open(previewVideo.videoType === "youtube" ? `https://www.youtube.com/watch?v=${previewVideo.embedId}` : `https://vimeo.com/${previewVideo.embedId}`, "_blank", "noopener,noreferrer")}>
                                    Open Video
                                </Button>
                                <Button type="primary" loading={importing} onClick={submitImport}>
                                    Import
                                </Button>
                            </div>
                        </Card>
                    ) : null}
                </div>
            )}

            {showCloseButton && (
                <div className="new-video__footer">
                    <Button onClick={onCancel}>Close</Button>
                </div>
            )}
        </div>
    );
};

export default connect(null, { createNewVideo })(NewVideo);
